import { getContrastColor } from '@gettactic/helpers/src/colors/getContrastColor';
import { getCssVariableValue } from '@gettactic/helpers/src/css/getCssVariableValue';
import Konva from 'konva';
import React from 'react';
import { Circle, Group, Line, Rect, Text } from 'react-konva';
import { MapInteractiveZone, SpotShape } from '../../../../types';
import { useMapCanvas } from '../../contexts/MapCanvasContext';
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_NODE_CIRCLE_RADIUS,
  DEFAULT_NODE_LABEL_ALIGNMENT
} from '../../defines';
import { getAbsolutePositionOfShape } from '../../helpers/getAbsolutePositionOfShape';
import { getTextProps } from '../../helpers/getTextProps';
import { ShapeEditingHighlightOptions, SpotLayout } from '../../types';
import { getEffectiveSpotAppearance } from '../../../../utils/spots';
import { computeAutomaticStateIconCenterOffset } from '../../../../utils/stateIconCentering';
import { useShape } from '../ShapeContext/useShape';
import { CircleSelectionNumber } from './CircleSelectionNumber';
import { InteractiveZone } from './InteractiveZone';
import { getInteractiveZoneStyles } from './helpers';
import { useSpotColor } from './useSpotColor';

const setStageContainerCursor = (
  e: Konva.KonvaEventObject<MouseEvent>,
  cursor: string
) => {
  const stage = e.target.getStage();
  if (stage) stage.container().style.cursor = cursor;
};

export interface SpotItemComponentProps {
  item: SpotShape;
  layout: SpotLayout;
  onSpotClick: (event: Konva.KonvaEventObject<PointerEvent>) => void;
  onShapeHighlight?: (highlight: ShapeEditingHighlightOptions) => void;
  isHighlighted?: boolean;
  interactiveZone?: MapInteractiveZone;
}

export const SpotItem = React.memo(
  ({
    item,
    layout,
    onSpotClick,
    interactiveZone,
    isHighlighted
  }: SpotItemComponentProps) => {
    const { isViewMode, highlightResource, setHoveringNode } = useMapCanvas();
    const { isHovered, isSelected, shape: _shape, selectIndex } = useShape();
    const shape = _shape as SpotShape;
    const { locked: isLocked } = shape;
    const rectColor = useSpotColor(shape);
    const [isSpotHovered, setIsSpotHovered] = React.useState(false);

    const labelColor = React.useMemo(() => {
      return getContrastColor(rectColor);
    }, [rectColor]);

    const shapeStroke = isHighlighted
      ? 'red'
      : !isLocked && (isHovered || isSelected)
        ? '#000000'
        : 'transparent';

    const strokeWidth = isHighlighted ? 2 : 0.5;
    const spotType = getEffectiveSpotAppearance(
      item.spotType || shape.spotType,
      item.spotAppearance || shape.spotAppearance
    );
    const labelPlacement = item.labelPlacement || shape.labelPlacement;

    const isRoundRoom = spotType === 'circle';
    const hasRect =
      spotType === 'desk' ||
      spotType === 'meeting_room' ||
      spotType === 'parking_lot' ||
      spotType === 'parking_space' ||
      isRoundRoom;

    let cornerRadius = item.rectProps?.cornerRadius || 0;
    if (spotType === 'meeting_room') {
      cornerRadius = 15;
    } else if (isRoundRoom) {
      cornerRadius = 40;
    }

    // Matches InteractiveZoneSVG/InteractiveZone: a spot type with no rect
    // of its own (Node) has nothing that naturally scales/centers with a
    // configured highlight area — the editor-only debug/select-order
    // markers below sit on the same dot InteractiveZone draws, so they need
    // the same auto-centering or they'd float away from it once the real
    // dot re-centers on a grid cell.
    const hasOwnRect = !!(layout.rect.width && layout.rect.height);
    const dotCenterOffset = !hasOwnRect
      ? computeAutomaticStateIconCenterOffset(shape)
      : null;
    const dotOffsetX = dotCenterOffset?.offsetX ?? 0;
    const dotOffsetY = dotCenterOffset?.offsetY ?? 0;
    const dotLayout: SpotLayout =
      dotOffsetX || dotOffsetY
        ? {
            ...layout,
            circle: {
              x: layout.circle.x + dotOffsetX,
              y: layout.circle.y + dotOffsetY
            }
          }
        : layout;

    const handleCircleMouseEnter = (
      e: Konva.KonvaEventObject<MouseEvent>
    ) => {
      if (shape.isInactive) return;
      setIsSpotHovered(true);
      setStageContainerCursor(e, 'pointer');

      const absPos = getAbsolutePositionOfShape(`${shape.id}-circle`);

      if (shape.resource_id && highlightResource)
        highlightResource(shape.resource_id, {
          x: absPos?.x || 0,
          y: absPos?.y || 0
        });

      setHoveringNode(shape);
    };

    const handleCircleMouseLeave = (
      e: Konva.KonvaEventObject<MouseEvent>
    ) => {
      if (shape.isInactive) return;
      setIsSpotHovered(false);
      setStageContainerCursor(e, '');

      if (shape.resource_id && highlightResource)
        highlightResource(null, { x: 0, y: 0 });

      setHoveringNode(null);
    };

    const circleRadius = item.circleProps?.radius || DEFAULT_NODE_CIRCLE_RADIUS;

    const textFontSize = shape.fontSize || 16;
    const textFontFamily = shape.fontFamily || DEFAULT_FONT_FAMILY;
    const textFontStyle = shape.textProps?.fontStyle || 'bold';
    const textRotation = item.textProps?.rotation ?? layout.text.rotation;
    const isLabelOutsideRect =
      labelPlacement === 'outside' || labelPlacement === 'outside-node';
    // Desk with left/right orientation + auto label uses the rect vertical
    // center as the label's anchor y. Konva renders text top-anchored, so
    // shift up by half the font size to visually center it regardless of
    // the configured font size.
    const isDeskVerticalAutoLabel =
      spotType === 'desk' &&
      (shape.orientation === 'left' || shape.orientation === 'right') &&
      (!shape.labelOrientation || shape.labelOrientation === 'auto') &&
      !isLabelOutsideRect;
    const textColor =
      item.textProps?.color ||
      (isLabelOutsideRect
        ? 'black'
        : labelColor === 'light'
          ? 'white'
          : 'black');

    const shapeWidth = (item.textProps?.width ?? layout.text.width) || 0;

    // Resolved availability-state highlight area, if configured.
    const stateArea = item.stateArea || shape.stateArea;
    let stateAreaRect: {
      x: number;
      y: number;
      width: number;
      height: number;
      cornerRadius?: number;
      rotation?: number;
    } | null = null;
    if (stateArea?.kind === 'spot-rect') {
      if (hasRect && layout.rect.width && layout.rect.height) {
        stateAreaRect = {
          x: layout.rect.x,
          y: layout.rect.y,
          width: layout.rect.width,
          height: layout.rect.height,
          cornerRadius,
          rotation: layout.rect.rotation
        };
      }
    } else if (
      stateArea?.kind === 'rect' &&
      stateArea.rect?.width &&
      stateArea.rect?.height
    ) {
      stateAreaRect = stateArea.rect;
    }
    // Drawn polygon area (spot-local points). Rendered from the very first
    // point in the editor so drawing gives live feedback.
    const stateAreaPoints =
      stateArea?.kind === 'polygon' && (stateArea.points?.length ?? 0) > 0
        ? (stateArea.points as { x: number; y: number }[])
        : null;
    const hasClosedPolygon = (stateAreaPoints?.length ?? 0) >= 3;
    // View-mode fill for the area, mirroring the SVG renderer: state color,
    // with the unavailable color as fallback when the dot state would be
    // transparent (reserved, not checked in).
    let stateAreaFill: string | null = null;
    const stateAreaFillOpacity = 0.2;
    if (isViewMode && interactiveZone && (stateAreaRect || hasClosedPolygon)) {
      const areaStyles = getInteractiveZoneStyles({
        isSelected: interactiveZone.isSelected || false,
        isLastAccessed: interactiveZone.isLastUsed || false,
        isOccupied: interactiveZone.isOccupied || false,
        notAssignedResource: interactiveZone.notAssigned || false,
        notAssignedUser: !interactiveZone.assignedUser,
        requiresApproval: interactiveZone.requiresApproval || false,
        isWalkUp: interactiveZone.isWalkUp || false,
        hasSchedules: interactiveZone.hasSchedules || false,
        showCircleProfilePhoto: interactiveZone.showCircleProfilePhoto || false,
        hasCheckedIn: interactiveZone.actualSchedule?.has_checked_in || false
      });
      // A configured `occupiedColor` wins over the computed color whenever
      // the resource is occupied/booked (mirrors the SVG viewer).
      stateAreaFill =
        interactiveZone.isOccupied && stateArea?.occupiedColor
          ? stateArea.occupiedColor
          : areaStyles.fill !== 'transparent'
            ? areaStyles.fill
            : getCssVariableValue('--map-spot-not-bookeable-unavailable-fill');
    }

    return (
      <Group
        id={item.id}
        onMouseEnter={handleCircleMouseEnter}
        onMouseLeave={handleCircleMouseLeave}
        onClick={onSpotClick}
        onTap={onSpotClick}
      >
        <Group>
          {isRoundRoom ? (
            <Circle
              x={layout.rect.x + layout.rect.width / 2}
              y={layout.rect.y + layout.rect.height / 2}
              radius={layout.rect.width / 2}
              fill={rectColor}
              opacity={item.rectProps?.opacity || 1}
              stroke={shapeStroke}
              strokeWidth={strokeWidth}
              perfectDrawEnabled={false}
            />
          ) : hasRect ? (
            <Rect
              x={layout.rect.x}
              y={layout.rect.y}
              width={layout.rect.width}
              height={layout.rect.height}
              rotation={layout.rect.rotation}
              cornerRadius={cornerRadius}
              fill={rectColor}
              opacity={item.rectProps?.opacity || 1}
              stroke={shapeStroke}
              strokeWidth={strokeWidth}
              perfectDrawEnabled={false}
            />
          ) : null}
          {/* Editor: dashed, clickable preview of the availability-state
              highlight area (the transparent fill makes its interior part
              of the spot's hit region, so clicking the area selects the
              spot). Viewer: state-colored tint, matching the SVG renderer. */}
          {!isViewMode && stateAreaRect ? (
            <Rect
              x={stateAreaRect.x}
              y={stateAreaRect.y}
              width={stateAreaRect.width}
              height={stateAreaRect.height}
              rotation={stateAreaRect.rotation || 0}
              cornerRadius={stateAreaRect.cornerRadius || 0}
              stroke="#2563eb"
              strokeWidth={1}
              dash={[6, 4]}
              fill="rgba(59, 130, 246, 0.12)"
              perfectDrawEnabled={false}
            />
          ) : null}
          {isViewMode && stateAreaRect && stateAreaFill ? (
            <Rect
              x={stateAreaRect.x}
              y={stateAreaRect.y}
              width={stateAreaRect.width}
              height={stateAreaRect.height}
              rotation={stateAreaRect.rotation || 0}
              cornerRadius={stateAreaRect.cornerRadius || 0}
              fill={stateAreaFill}
              stroke={stateAreaFill}
              strokeWidth={1.5}
              opacity={stateAreaFillOpacity}
              perfectDrawEnabled={false}
            />
          ) : null}
          {!isViewMode && stateAreaPoints ? (
            <Line
              points={stateAreaPoints.flatMap((point) => [point.x, point.y])}
              closed={hasClosedPolygon}
              stroke="#2563eb"
              strokeWidth={1}
              dash={[6, 4]}
              fill={hasClosedPolygon ? 'rgba(59, 130, 246, 0.12)' : undefined}
              perfectDrawEnabled={false}
            />
          ) : null}
          {isViewMode && hasClosedPolygon && stateAreaPoints && stateAreaFill ? (
            <Line
              points={stateAreaPoints.flatMap((point) => [point.x, point.y])}
              closed
              fill={stateAreaFill}
              stroke={stateAreaFill}
              strokeWidth={1.5}
              opacity={stateAreaFillOpacity}
              perfectDrawEnabled={false}
            />
          ) : null}
          {labelPlacement !== 'hidden' && (
            <Text
              x={layout.text.x + (item.textProps?.marginLeft || 0)}
              y={
                layout.text.y +
                (item.textProps?.marginTop || 0) -
                (spotType === 'meeting_room' || spotType === 'circle'
                  ? textFontSize > 26
                    ? textFontSize / 2
                    : 0
                  : isDeskVerticalAutoLabel
                    ? textFontSize / 2 - 4
                    : 0)
              }
              text={item.label}
              width={spotType === 'meeting_room' || spotType === 'circle' ? undefined : shapeWidth}
              fontStyle={textFontStyle}
              rotation={textRotation}
              perfectDrawEnabled={false}
              ref={(node) => {
                if (
                  node &&
                  (shape.orientation === 'top' ||
                    shape.orientation === 'bottom')
                ) {
                  const textWidth = node.width();
                  if (shape.textAlign === 'center') {
                    node.x((shapeWidth - textWidth) / 2);
                  }
                }
              }}
              {...getTextProps(shape, {
                defaultTextColor: textColor,
                defaultFontFamily: textFontFamily,
                defaultFontSize: textFontSize,
                defaultTextAlign: DEFAULT_NODE_LABEL_ALIGNMENT
              })}
            />
          )}
        </Group>
        <Group
          x={layout.circle.x}
          y={layout.circle.y}
          offsetX={layout.circle.x}
          offsetY={layout.circle.y}
          rotation={-(shape.rotation || 0)}
        >
          <InteractiveZone
            interactiveZone={interactiveZone}
            shape={shape}
            layout={layout}
            onClick={() => null}
            selectIndex={selectIndex}
            isActive={!shape.isInactive}
            isSpotHovered={isSpotHovered}
          />
          {selectIndex === undefined && !isViewMode && (
            <Circle
              x={dotLayout.circle.x}
              y={dotLayout.circle.y}
              width={10}
              height={10}
              fill={item.resource_id ? '#60bb10' : 'orange'}
              opacity={0.5}
              perfectDrawEnabled={false}
            />
          )}
          {selectIndex !== undefined && (
            <CircleSelectionNumber
              layout={dotLayout}
              label={String(selectIndex + 1)}
              radius={circleRadius}
              shape={shape}
            />
          )}
        </Group>
      </Group>
    );
  }
);
